import { effectivePrintDpi, formatUsd, priceForCents, RECOMMENDED_PRINT_DPI } from '@lumina/shared';

/** A size made in one frame, as Gelato produces it (print_products row). */
export interface CatalogProduct {
  gelato_product_uid: string;
  /** Paper / texture, e.g. "200 gsm 80lb uncoated". */
  paper: string | null;
  /** What Gelato charges us per unit, excluding shipping. */
  cost_cents: number | null;
  cost_currency: string;
  cost_source: 'gelato' | 'manual' | null;
  cost_updated_at: string | null;
  /** Last Verify: 1 found in Gelato, 0 not found, null unchecked. */
  gelato_ok: number | null;
  gelato_error: string | null;
  gelato_checked_at: string | null;
  /** Cheapest standard US shipping for one unit (scripts/sync-gelato-products.mjs). */
  ship_estimate_cents: number | null;
  ship_estimate_method: string | null;
}

/** Row from /api/admin/print-sizes. */
export interface CatalogSize {
  id: string;
  label: string;
  width_in: number;
  height_in: number;
  multiplier: number;
  min_dpi: number;
  sort_order: number;
  is_active: number;
  photo_count?: number;
  gelato_template_id?: string | null;
  /** Keyed by frame id; a frame without a product isn't sold at this size. */
  products: Record<string, CatalogProduct>;
}

export interface Assignment {
  min_dpi_override: number | null;
}

export type Assignments = Record<string, Assignment>;

/** Overrides outside this range are rejected by the API. */
export const DPI_OVERRIDE_MIN = 72;
export const DPI_OVERRIDE_MAX = 600;

export interface PairStatus {
  dpi: number;
  threshold: number;
  offered: boolean;
  /** Why an assigned pair isn't offered, if it isn't. */
  reason: string | null;
}

// Same rule as workers/api/utils/print-sizes.js: offered when assigned, the
// size is active, and DPI meets the override or the size default.
export function pairStatus(
  photo: { width: number; height: number },
  size: CatalogSize,
  assignment: Assignment | undefined
): PairStatus {
  const dpi = effectivePrintDpi(photo, { widthIn: size.width_in, heightIn: size.height_in });
  const threshold = assignment?.min_dpi_override ?? size.min_dpi;
  let reason: string | null = null;
  if (!assignment) reason = 'Not assigned';
  else if (!size.is_active) reason = 'Size inactive';
  else if (dpi < threshold) reason = `${dpi} DPI is below ${threshold}`;
  else if (Object.keys(size.products).length === 0) reason = 'No Gelato products';
  return { dpi, threshold, offered: reason === null, reason };
}

export function dpiColor(dpi: number, threshold: number) {
  if (dpi >= RECOMMENDED_PRINT_DPI) return '#15803d';
  if (dpi >= threshold) return '#b45309';
  return '#b91c1c';
}

/**
 * One photo × size pairing: assign checkbox, effective DPI, the print-only
 * price (when `basePriceCents` is given), and an optional DPI threshold
 * override (blank = the size's default).
 */
export function AssignmentCell({
  photo,
  size,
  assignment,
  onChange,
  basePriceCents,
  compact = false,
}: {
  photo: { width: number; height: number };
  size: CatalogSize;
  assignment: Assignment | undefined;
  onChange: (next: Assignment | undefined) => void;
  basePriceCents?: number;
  compact?: boolean;
}) {
  const status = pairStatus(photo, size, assignment);
  const printPrice =
    basePriceCents == null ? null : priceForCents({ priceBaseCents: basePriceCents }, size, 'none');
  const assigned = !!assignment;
  const override = assignment?.min_dpi_override ?? null;
  const overrideInvalid =
    override !== null && (override < DPI_OVERRIDE_MIN || override > DPI_OVERRIDE_MAX);

  return (
    <div
      title={status.reason ?? `Offered — ${status.dpi} DPI (threshold ${status.threshold})`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: compact ? 6 : 10,
        borderRadius: 6,
        border: '1px solid',
        borderColor: status.offered ? '#bbf7d0' : assigned ? '#fde68a' : '#eee',
        background: status.offered ? '#f0fdf4' : assigned ? '#fffbeb' : '#fafafa',
        minWidth: compact ? 92 : 160,
      }}
    >
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={assigned}
          onChange={(e) => onChange(e.target.checked ? { min_dpi_override: null } : undefined)}
        />
        {!compact && <span style={{ fontWeight: 500 }}>{size.label}</span>}
        <span style={{ fontSize: 12, color: dpiColor(status.dpi, status.threshold), marginLeft: 'auto' }}>
          {status.dpi} DPI
        </span>
      </label>
      {printPrice != null && (
        <span
          title="Print only — frames add their own price"
          style={{ fontSize: 12, color: assigned ? '#1a1a1a' : '#9ca3af' }}
        >
          {formatUsd(printPrice)}
          {Object.keys(size.products).length > 1 && <span style={{ color: '#9ca3af' }}>+</span>}
        </span>
      )}
      {assigned && (
        <input
          type="number"
          min={DPI_OVERRIDE_MIN}
          max={DPI_OVERRIDE_MAX}
          placeholder={`min ${size.min_dpi}`}
          value={override ?? ''}
          aria-label={`Minimum DPI override for ${size.label}`}
          onChange={(e) => {
            const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
            onChange({ min_dpi_override: Number.isNaN(v) ? null : v });
          }}
          style={{
            width: '100%',
            padding: '3px 6px',
            fontSize: 12,
            border: `1px solid ${overrideInvalid ? '#fca5a5' : '#e5e5e5'}`,
            borderRadius: 4,
            boxSizing: 'border-box',
          }}
        />
      )}
      {!compact && status.reason && assigned && (
        <span style={{ fontSize: 11, color: '#b45309' }}>{status.reason}</span>
      )}
    </div>
  );
}

export function toAssignmentList(assignments: Assignments) {
  return Object.entries(assignments).map(([size_id, a]) => ({
    size_id,
    min_dpi_override: a.min_dpi_override,
  }));
}

export function hasInvalidOverride(assignments: Assignments) {
  return Object.values(assignments).some(
    (a) =>
      a.min_dpi_override !== null &&
      (a.min_dpi_override < DPI_OVERRIDE_MIN || a.min_dpi_override > DPI_OVERRIDE_MAX)
  );
}
